/**
 * Verticales de producto (FLAGS) — features "paraguas" que una organización
 * enciende o apaga como bloque al desplegar Space Academy progresivamente:
 * el core estable arranca encendido y cada vertical pulida se habilita por
 * academia cuando está lista.
 *
 * Cada vertical declara sus consecuencias (`implications`) y su lugar en el
 * grafo de dependencias (`requires`). Los datos SIEMPRE se preservan al
 * apagar: se bloquean escrituras nuevas y se oculta la UI, nada se borra.
 *
 * Estas features viajan por el manifest TS (`pnpm gen:features`) al catálogo
 * Rust y se reconcilian como filas togglables de `feature_flags` al boot.
 */

import type { FeatureManifest } from "./country/packs";

const BILLING_CORE: FeatureManifest = {
  key: "billing.core",
  name: "Caja y facturación",
  description:
    "Vertical de cobros de matrícula: facturas y cuotas, registro de pagos (efectivo, pago móvil, transferencia, Zelle, Stripe), verificación, tasas del día y recibos.",
  scope: "org",
  status: "stable",
  gateable: true,
  since: "2026-06",
  docsSlug: "caja",
  implications: {
    navHidden: ["Caja (/finance)", "Mis pagos (/payments)"],
    flowsBlocked: [
      "Registrar y verificar pagos",
      "Emitir facturas y planes de pago",
      "Checkout online (Stripe)",
      "Actualizar la tasa del día",
    ],
    dataPolicy: "preserved",
  },
};

/**
 * B4 · G1/G3/G4 — «Adelanta y ahorra».
 *
 * Regla de negocio del dueño: el alumno cursó y pagó el primer mes, y entonces
 * se le ofrece pagar por adelantado los meses restantes con un porcentaje de
 * descuento. El motivo declarado es el flujo de caja, así que la métrica que la
 * justifica (cobrado por adelantado vs proyectado) va en la misma vertical.
 *
 * Apagarla NO toca ningún prepago ya emitido: una factura es dinero y el dato
 * se preserva siempre — se cierra la oferta, no la contabilidad.
 */
const BILLING_PREPAY: FeatureManifest = {
  key: "billing.prepay",
  name: "Prepago con descuento",
  description:
    "Oferta «Adelanta y ahorra»: el alumno paga por adelantado las cuotas que le quedan con un descuento escalado, y la academia ve el cuadro de caja (cobrado por adelantado vs proyectado).",
  scope: "org",
  status: "beta",
  gateable: true,
  since: "2026-07",
  docsSlug: "caja",
  // Arista BLANDA sobre la vertical de caja: el prepago compone con ella, pero
  // apagar la caja entera no puede quedar rehén de esta oferta.
  requires: ["billing.core?"],
  implications: {
    navHidden: ["Tarjeta «Adelanta y ahorra» en /payments y en el curso"],
    flowsBlocked: [
      "Ofrecer y aceptar el prepago con descuento",
      "Cuadro de caja de cobros adelantados",
    ],
    dataPolicy: "preserved",
  },
};

const CRM_LEADS: FeatureManifest = {
  key: "crm.leads",
  name: "CRM y captación",
  description:
    "Vertical de captación: leads con UTM, pipeline kanban, timeline de contactos, conversión a alumno y formulario público de interés.",
  scope: "org",
  status: "stable",
  gateable: true,
  since: "2026-06",
  docsSlug: "captacion",
  implications: {
    navHidden: ["CRM (/crm)"],
    flowsBlocked: [
      "Crear y editar leads",
      "Registrar eventos de contacto",
      "Convertir leads en alumnos",
      "Formulario público de interés",
    ],
    dataPolicy: "preserved",
  },
};

const CRM_CAMPAIGNS: FeatureManifest = {
  key: "crm.campaigns",
  name: "Campañas segmentadas",
  description:
    "Campañas de email dirigidas a segmentos del CRM (deuda, inactividad, interés). Requiere el CRM y las campañas de email.",
  scope: "org",
  status: "stable",
  gateable: true,
  since: "2026-06",
  docsSlug: "captacion",
  requires: ["crm.leads", "email.campaigns"],
  implications: {
    navHidden: ["Segmentos y campañas dentro del CRM"],
    flowsBlocked: ["Crear segmentos", "Programar/enviar campañas segmentadas"],
    dataPolicy: "preserved",
  },
};

const EMAIL_CAMPAIGNS: FeatureManifest = {
  key: "email.campaigns",
  name: "Campañas de email",
  description:
    "Envío de campañas de email desde el back-office (plantillas, programación, fan-out por destinatarios).",
  scope: "platform",
  status: "stable",
  gateable: true,
  since: "2026-06",
  docsSlug: "administrador",
  implications: {
    navHidden: ["Email marketing en el admin"],
    flowsBlocked: ["Crear, programar y enviar campañas de email"],
    dataPolicy: "preserved",
  },
};

const GAMIFICATION_CORE: FeatureManifest = {
  key: "gamification.core",
  name: "Gamificación (XP, rachas, ligas)",
  description:
    "Capa de motivación: XP por lecciones, rachas diarias, ligas y tabla de posiciones. El progreso académico NO depende de ella.",
  scope: "org",
  status: "stable",
  since: "2026-06",
  docsSlug: "alumno",
  implications: {
    navHidden: ["Ligas (/leaderboard)"],
    flowsBlocked: [],
    dataPolicy: "preserved",
  },
};

const GAMIFICATION_BADGES: FeatureManifest = {
  key: "gamification.badges",
  name: "Insignias",
  description:
    "Insignias otorgadas por hitos (participación en foros, rachas, logros de cohorte). Requiere la gamificación base.",
  scope: "org",
  status: "stable",
  gateable: true,
  since: "2026-06",
  docsSlug: "alumno",
  requires: ["gamification.core"],
  implications: {
    navHidden: ["Vitrina de insignias del perfil"],
    flowsBlocked: ["Otorgar insignias nuevas"],
    dataPolicy: "preserved",
  },
};

const MEDIA_RECORDING: FeatureManifest = {
  key: "media.recording",
  name: "Grabaciones de clases (VOD)",
  description:
    "Enlazar la grabación de una clase en vivo como vídeo re-visitable por la cohorte con el player propio. Requiere el bloque de vídeo y las clases en vivo.",
  scope: "org",
  status: "stable",
  gateable: true,
  since: "2026-06",
  docsSlug: "profesor",
  requires: ["media.block.video", "academy.module.live_session"],
  implications: {
    navHidden: ["Sección «Grabación» de la sesión en vivo"],
    flowsBlocked: ["Adjuntar grabaciones a sesiones"],
    dataPolicy: "preserved",
  },
};

const FLIPPED_SESSIONS: FeatureManifest = {
  key: "academy.flipped_sessions",
  name: "Aula invertida (plan de clase)",
  description:
    "Organiza los módulos de la cohorte alrededor de cada clase en vivo con una fase (antes/durante/después): el profesor arma el plan de clase y el alumno ve una ruta semana a semana con sus tareas marcadas.",
  scope: "org",
  status: "stable",
  gateable: true,
  since: "2026-07",
  docsSlug: "alumno",
  // Dependencia BLANDA del module kind clase-en-vivo: el plan compone mejor con
  // él (la fase «durante» enlaza la sesión), pero no queda rehén — la clase en
  // vivo es la propia class_session del horario (core), así que apagar el kind
  // no invalida el plan de clase.
  requires: ["academy.module.live_session?"],
  implications: {
    navHidden: [],
    flowsBlocked: [
      "Armar el plan de clase de una sesión (antes/durante/después)",
      "Ver la ruta de aula invertida de una clase",
    ],
    dataPolicy: "preserved",
  },
};

const TUTORING: FeatureManifest = {
  key: "academy.tutoring",
  name: "Tutorías (corrección y reentrega)",
  description:
    "Rol de tutor con cola de corrección y SLA, y ciclo entrega → feedback → reentrega con historial de versiones. El tutor corrige y devuelve entregas de sus cohortes; el alumno reentrega hasta aprobar.",
  scope: "org",
  status: "stable",
  gateable: true,
  since: "2026-07",
  docsSlug: "alumno",
  // Dependencia BLANDA del kind tarea: el ciclo de reentrega opera sobre entregas
  // de assignments, pero no queda rehén si se apaga el kind.
  requires: ["academy.module.assignment?"],
  implications: {
    navHidden: ["Tutorías"],
    flowsBlocked: [
      "Panel de tutorías (cola de corrección con SLA)",
      "Reentregar un desafío tras la devolución del tutor",
    ],
    dataPolicy: "preserved",
  },
};

const AI_TUTOR: FeatureManifest = {
  key: "academy.ai_tutor",
  name: "Tutor IA 24/7",
  description:
    "Tutor con IA que responde preguntas del alumno con el material del curso y da feedback PRELIMINAR de entregas antes de la corrección humana (borrador de la IA, no es la nota). Motor Anthropic Claude configurado por organización vía la integración de IA. Con cuota mensual de tokens por academia.",
  scope: "org",
  status: "beta",
  gateable: true,
  since: "2026-07",
  docsSlug: "tutor-ia",
  // La dependencia de la integración de IA es de CONFIG (display-only): no es una
  // arista del grafo de flags (las integraciones no son nodos del grafo TS). El
  // gate en runtime comprueba que la credencial esté configurada — si no, el
  // endpoint responde "no disponible" (503) en vez de 403.
  implications: {
    navHidden: [
      "Tutor IA (/ai-tutor)",
      "Botón «Pregúntale al tutor»",
      "Botón «Pedir feedback de la IA»",
    ],
    flowsBlocked: [
      "Chatear con el tutor IA",
      "Pedir feedback preliminar de la IA sobre una entrega",
    ],
    dataPolicy: "preserved",
  },
};

/**
 * B8 · Chatbot COMERCIAL de la academia. Es otro consumidor, no el tutor: aquel
 * responde con el material del curso a un alumno matriculado; este responde
 * precios y horarios a gente **sin cuenta**.
 *
 * Nace apagada, como las cuatro de autoría: encender una IA que gasta dinero es
 * una decisión del dueño, no un default heredado.
 */
const AI_CHATBOT: FeatureManifest = {
  key: "academy.ai_chatbot",
  name: "Asistente de la academia (leads)",
  description:
    "Chatbot público que responde precios, horarios, modalidades, formas de pago, requisitos y certificados a personas SIN cuenta, citando la FAQ de la academia y el catálogo en vivo. Solo INFORMA: no pre-matricula, no genera enlaces de pago y no agenda. Cuando la confianza baja o lo pide el interesado, escala a WhatsApp y crea un lead en el CRM con la transcripción.",
  scope: "org",
  status: "beta",
  gateable: true,
  defaultEnabled: false,
  since: "2026-07",
  docsSlug: "asistente-academia",
  // Blanda: sin CRM el chatbot sigue respondiendo y ofreciendo WhatsApp; lo que
  // se pierde es el lead con la transcripción. No es motivo para bloquear.
  requires: ["crm.leads?"],
  implications: {
    navHidden: [
      "Asistente de la academia (widget público)",
      "Panel de deflexión (/admin/academy-bot)",
    ],
    flowsBlocked: [
      "Preguntar al asistente sin tener cuenta",
      "Escalar una conversación a WhatsApp con lead automático",
    ],
    dataPolicy: "preserved",
  },
};

/** B8 · Generar la estructura de un curso y el contenido de sus bloques. */
const AI_AUTHORING: FeatureManifest = {
  key: "academy.ai_authoring",
  name: "Autoría de cursos con IA",
  description:
    "Convierte un encargo en lenguaje natural («Marketing digital, nivel 1, 3 meses, 2 clases por semana») en una PROPUESTA de programa, curso, unidades, módulos y bloques. La IA nunca escribe en la base de datos: produce una propuesta que se valida contra los registros reales de bloques y de módulos, y que una persona aprueba pieza a pieza. Aplicarla usa las mismas mutaciones que el editor, así que actividad, papelera y deshacer funcionan igual.",
  scope: "org",
  status: "beta",
  gateable: true,
  defaultEnabled: false,
  since: "2026-07",
  docsSlug: "autoria-ia",
  implications: {
    navHidden: ["Generar con IA (editor de cursos)", "Propuestas de IA (/ai-authoring)"],
    flowsBlocked: [
      "Generar la estructura de un curso con IA",
      "Generar el contenido de los bloques de una lección con IA",
    ],
    dataPolicy: "preserved",
  },
};

/** B8 · Proponer un diff sobre un curso que YA existe. */
const AI_AUTHORING_EDIT: FeatureManifest = {
  key: "academy.ai_authoring.edit",
  name: "Edición de cursos con IA",
  description:
    "Propone cambios sobre un curso existente como un diff pieza a pieza (añadir unidad, reescribir un bloque, reordenar módulos, retirar un recurso), con previsualización y aprobación o rechazo por partes. Razonar sobre lo ya construido sin romperlo es más difícil que crear desde cero: por eso lleva su propio interruptor.",
  scope: "org",
  status: "beta",
  gateable: true,
  defaultEnabled: false,
  since: "2026-07",
  docsSlug: "autoria-ia",
  requires: ["academy.ai_authoring"],
  implications: {
    navHidden: ["Proponer cambios con IA (editor de cursos)"],
    flowsBlocked: ["Pedir a la IA un diff sobre un curso existente"],
    dataPolicy: "preserved",
  },
};

/** B8 · Generar CONFIGURACIONES (rúbricas, reglas de completitud, plan de clase). */
const AI_AUTHORING_CONFIG: FeatureManifest = {
  key: "academy.ai_authoring.config",
  name: "Configuraciones generadas con IA",
  description:
    "Propone rúbricas, reglas de aprobación, esquemas de nota y plan de clase. Ninguna configuración generada evita el gate de su propia feature, y nada que toque la nota o el certificado se genera con un modelo barato: una rúbrica sutilmente mal hecha propaga a todas las notas de la cohorte y nadie la detecta leyéndola.",
  scope: "org",
  status: "experimental",
  gateable: true,
  defaultEnabled: false,
  since: "2026-07",
  docsSlug: "autoria-ia",
  requires: ["academy.ai_authoring"],
  implications: {
    navHidden: ["Generar rúbrica con IA", "Generar reglas de aprobación con IA"],
    flowsBlocked: ["Pedir a la IA una rúbrica, un esquema de nota o un plan de clase"],
    dataPolicy: "preserved",
  },
};

/**
 * B8 · Aplicar una propuesta SIN aprobación humana.
 *
 * Existe para que su ausencia sea explícita y comprobable. **Se queda apagada en
 * v1** y hay un test que lo verifica: la revisión humana es la mitad barata de
 * la regla del híbrido, y quitarla sin decirlo sería el fallo silencioso más
 * caro de toda esta feature.
 */
const AI_AUTHORING_AUTOAPPLY: FeatureManifest = {
  key: "academy.ai_authoring.autoapply",
  name: "Aplicar propuestas de IA sin revisión (no usar en v1)",
  description:
    "Permitiría aplicar una propuesta de la IA sin que una persona la apruebe. APAGADA de fábrica y así se queda en la v1: la revisión humana es lo que hace aceptable generar contenido con un modelo barato. Existe declarada para que su ausencia sea una decisión visible y testeable, no un olvido.",
  scope: "org",
  status: "experimental",
  // No gateable: no gatea una escritura, sino la AUSENCIA de una revisión. Su
  // garantía es el test que comprueba que sigue resolviendo apagada.
  gateable: false,
  defaultEnabled: false,
  since: "2026-07",
  docsSlug: "autoria-ia",
  requires: ["academy.ai_authoring"],
};

const COMPLETION_RULES: FeatureManifest = {
  key: "academy.completion_rules",
  name: "Reglas de aprobación",
  description:
    "Motor de criterios configurables que decide quién APRUEBA una cohorte y recibe certificado al cerrarla: asistencia mínima, desafíos aprobados, proyecto final, nota mínima y marca del instructor (combinables con Y/O). Sin reglas, se conserva la puerta de nota heredada.",
  scope: "course",
  status: "beta",
  gateable: true,
  since: "2026-07",
  docsSlug: "administrador",
  implications: {
    navHidden: ["Editor de reglas de aprobación (ajustes de curso/cohorte)"],
    flowsBlocked: [
      "Editar las reglas de aprobación de un curso/cohorte",
      "Marcar manualmente a un alumno como completo",
      "Auto-certificar en el cierre según reglas (sin la feature se usa la puerta de nota)",
    ],
    dataPolicy: "preserved",
  },
};

const FINAL_PROJECT: FeatureManifest = {
  key: "academy.final_project",
  name: "Proyecto final",
  description:
    "Formaliza el proyecto final como hito de la cohorte: la academia DESIGNA un trabajo como EL proyecto final del curso, publica su rúbrica para que el alumno la vea ANTES de entregar (criterios, niveles y puntos), fija una fecha límite por cohorte y, al cerrar, solo certifica a quien lo aprueba. Corrección de un solo intento opcional (estilo CoderHouse).",
  scope: "course",
  status: "beta",
  gateable: true,
  since: "2026-07",
  docsSlug: "alumno",
  // Dependencias BLANDAS: el proyecto final compone con las reglas de aprobación
  // (aporta el criterio `final_project`) y con el módulo de evaluación (el
  // proyecto ES un assignment), pero no queda rehén de ninguno — apagar el
  // requisito no fuerza apagar el proyecto final.
  requires: ["academy.completion_rules?", "academy.module.assignment?"],
  implications: {
    navHidden: ["Proyecto final (/final-project)"],
    flowsBlocked: [
      "Designar un assignment como el proyecto final del curso",
      "Publicar la rúbrica del proyecto final al alumno",
      "Entregar el proyecto final",
    ],
    // Los datos SIEMPRE se preservan: entregas y notas quedan; sólo se bloquean
    // las escrituras nuevas y se oculta la UI.
    dataPolicy: "preserved",
  },
};

const REST_ORG_CATALOG: FeatureManifest = {
  key: "academy.data.rest_org_catalog",
  name: "Catálogo de la academia por API",
  description:
    "El catálogo interno de la academia (qué cursos ofrece, su árbol de contenido y publicar/despublicar) se pide al servidor en vez de replicarse al dispositivo. Cierra la fuga por la que TODO miembro —incluido un alumno— recibía offline el árbol completo de todos los cursos de la academia, borradores y cursos borrados incluidos. El alumno conserva offline el curso en el que está matriculado.",
  scope: "org",
  status: "beta",
  gateable: true,
  since: "2026-07",
  docsSlug: "administrador",
  implications: {
    navHidden: [],
    flowsBlocked: [
      "Publicar un curso en la academia desde la API",
      "Despublicar un curso de la academia desde la API",
    ],
    dataPolicy: "preserved",
  },
};

/**
 * §L1 · Modalidad PRESENCIAL — la única vertical que nace APAGADA.
 *
 * La academia es online-only por decisión de negocio: «la plataforma solo debe
 * contener cursos online, sin excepción». Lo presencial NO se borra — sedes,
 * aulas, ocupación, pase de lista con QR y las modalidades `in_person`/`hybrid`
 * siguen implementadas, con sus tests corriendo y sus datos intactos
 * (`dataPolicy: preserved`) — pero la plataforma no los ofrece hasta que la
 * academia crezca y alguien encienda el flag a propósito.
 *
 * `defaultEnabled: false` es lo que la distingue del resto: sin fila en
 * `feature_flags` ni override, resuelve APAGADA (el resto resuelve encendida).
 */
const MODALITY_IN_PERSON: FeatureManifest = {
  key: "academy.modality.in_person",
  name: "Modalidad presencial (sedes, aulas y pase de lista)",
  description:
    "Clases con asistencia física: sedes y aulas de la academia, mapa de ocupación, pase de lista con código QR y las modalidades «presencial» e «híbrida» de una cohorte. Construida y probada, pero APAGADA de fábrica: esta academia es 100 % online. Encenderla no crea nada nuevo — devuelve a la vista lo que ya existe.",
  scope: "org",
  status: "stable",
  gateable: true,
  defaultEnabled: false,
  since: "2026-07",
  docsSlug: "administrador",
  implications: {
    navHidden: ["Sedes (/academy/campuses)", "Pase de lista (/checkin)"],
    flowsBlocked: [
      "Crear y editar sedes y aulas",
      "Abrir el pase de lista con código QR de una clase",
      "Registrar asistencia presencial con el código de la clase",
      "Elegir modalidad «presencial» o «híbrida» al crear o editar una cohorte",
    ],
    dataPolicy: "preserved",
  },
};

/**
 * B6 · NIVELES — el paraguas. Rutas, etapas y estado del alumno.
 *
 * Nace APAGADA: publicar una ruta cambia lo que el alumno PUEDE abrir, y eso no
 * puede encenderse solo. El backfill deja todas las rutas en borrador
 * precisamente por lo mismo.
 */
const PROGRESSION: FeatureManifest = {
  key: "academy.progression",
  name: "Rutas de progresión (niveles)",
  description:
    "Convierte el plan de estudios en una ruta de niveles con desbloqueo configurable. Por defecto un nivel es un curso dentro de un programa, pero la academia puede elegir otro origen —un programa entero, una unidad, o niveles definidos a mano— sin tocar código.",
  scope: "org",
  status: "beta",
  gateable: true,
  defaultEnabled: false,
  since: "2026-07",
  docsSlug: "alumno",
  implications: {
    navHidden: ["Mi ruta (/path)"],
    flowsBlocked: [
      "Crear y publicar rutas de progresión",
      "Reconciliar las etapas de una ruta con su programa",
      "Editar el desbloqueo o las recompensas de una etapa",
    ],
    dataPolicy: "preserved",
  },
};

// ─── B2 · COMUNICACIÓN DEL AULA (plan docs/plans/02-chat-aula.md) ─────────────
//
// Cinco flags, una por etapa, porque tienen perfiles de COSTE distintos y el
// dueño pidió poder apagar cada parte por separado ante un problema de factura.
// El orden del grafo va de lo gratis a lo caro:
//
//   roster (REST, ~0)  →  chat (réplica por cohorte)  →  promover (~0)
//                                                     →  menciones (0 filas)
//                                                     →  reacciones (+20 % de
//                                                        entregas ⇒ nace OFF)
//
// AVISO que vale para las cinco: apagar un flag NO recorta un stream (decisión
// explícita de `platform/features.rs`: recortar streams por feature rompe
// checkpoints y cachés offline). El kill-switch DE COSTE del chat es la ventana
// de retención (`CHAT_RETENTION_DAYS`); el flag apaga la ESCRITURA y la UI.

const CLASSROOM_ROSTER: FeatureManifest = {
  key: "academy.classroom.roster",
  name: "Compañeros del aula",
  description:
    "El alumno ve quién más está en su cohorte: nombre para mostrar y avatar, nada más. NUNCA correo, cédula, teléfono, estado de matrícula ni notas — eso sigue siendo expediente y solo lo ve quien gestiona. Se sirve por API (`GET /api/cohorts/:id/roster`), no por réplica: identidad de terceros no viaja al dispositivo.",
  scope: "org",
  status: "beta",
  gateable: true,
  since: "2026-07",
  docsSlug: "alumno",
  implications: {
    navHidden: ["Compañeros (dentro del aula)"],
    flowsBlocked: [
      "Ver la lista de compañeros de la cohorte",
      "Ver el nombre y el avatar de quien escribe en el chat del aula",
    ],
    dataPolicy: "preserved",
  },
};

/**
 * **Solo el camino visual.** El motor sigue calculando: apagarla devuelve
 * `/learn` a la lista de siempre sin perder estado ni certificados.
 *
 * No es `gateable`: no bloquea ninguna escritura, decide si el cliente dibuja
 * el mapa. Inventarle un gate en un write-path que no existe sería mentirle al
 * test de completitud de `GATED_KEYS`.
 */
const PROGRESSION_PATH: FeatureManifest = {
  key: "academy.progression.path",
  name: "Camino visual de la ruta",
  description:
    "Mapa de niveles en serpiente con nodos por clase o módulo e hitos de fin de mes, estados con icono y texto (nunca solo color) y explicación de por qué un nivel está cerrado.",
  scope: "org",
  status: "beta",
  since: "2026-07",
  docsSlug: "alumno",
  requires: ["academy.progression"],
};

/**
 * **Solo el bloqueo.** Apagada, toda etapa queda disponible y el
 * `unlock_config` se conserva sin evaluarse: apagar no borra ni una regla.
 */
const PROGRESSION_PREREQUISITES: FeatureManifest = {
  key: "academy.progression.prerequisites",
  name: "Prerrequisitos entre niveles",
  description:
    "Aplica el árbol de desbloqueo de cada nivel. Apagada, todos los niveles quedan disponibles y la configuración se conserva intacta para cuando se vuelva a encender.",
  scope: "org",
  status: "beta",
  since: "2026-07",
  docsSlug: "alumno",
  requires: ["academy.progression"],
};

/**
 * El SALTO DE NIVEL es una **excepción, no la norma**: nace apagada y la
 * academia la habilita cuando decide que se puede entrar sin cursar lo anterior.
 */
const PROGRESSION_PLACEMENT: FeatureManifest = {
  key: "academy.progression.placement",
  name: "Prueba de nivelación (salto de nivel)",
  description:
    "Permite entrar a un nivel sin haber cursado el anterior aprobando una prueba. La nota la pone el servidor al corregir. Nace apagada: saltarse un nivel es la excepción, no el camino.",
  scope: "org",
  status: "beta",
  defaultEnabled: false,
  since: "2026-07",
  gateable: true,
  docsSlug: "alumno",
  requires: ["academy.progression.prerequisites"],
  implications: {
    navHidden: [],
    flowsBlocked: ["Registrar el resultado de una prueba de nivelación"],
    dataPolicy: "preserved",
  },
};

const CLASSROOM_CHAT: FeatureManifest = {
  key: "academy.classroom.chat",
  name: "Chat del aula",
  description:
    "Canal plano por cohorte con marca de leído, moderación del profesorado (ocultar un mensaje lo saca del dispositivo del alumno) y VENTANA DE RETENCIÓN: pasados los días configurados, los mensajes salen de la réplica sin borrarse. Convive con el foro, que sigue guardando lo que merece hilo permanente.",
  scope: "org",
  status: "beta",
  gateable: true,
  since: "2026-07",
  docsSlug: "alumno",
  // DURA: un chat sin nombres es inusable y su moderación, inejercible.
  requires: ["academy.classroom.roster"],
  implications: {
    navHidden: ["Chat (pestaña del aula)"],
    flowsBlocked: [
      "Enviar mensajes en el chat del aula",
      "Moderar el chat del aula (ocultar y restaurar mensajes)",
    ],
    dataPolicy: "preserved",
  },
};

const CLASSROOM_CHAT_PROMOTE: FeatureManifest = {
  key: "academy.classroom.chat.promote",
  name: "Destacar y promover mensajes del chat",
  description:
    "El profesorado fija mensajes (quedan exentos del archivado) y promueve un mensaje a hilo del foro de la cohorte. Es lo que hace legítimo vaciar el chat: lo que valía ya está en el foro.",
  scope: "org",
  status: "beta",
  gateable: true,
  since: "2026-07",
  docsSlug: "alumno",
  requires: ["academy.classroom.chat"],
  implications: {
    navHidden: [],
    flowsBlocked: [
      "Fijar un mensaje del chat del aula",
      "Promover un mensaje del chat a hilo del foro",
    ],
    dataPolicy: "preserved",
  },
};

const CLASSROOM_CHAT_MENTIONS: FeatureManifest = {
  key: "academy.classroom.chat.mentions",
  name: "Menciones en el chat del aula",
  description:
    "Escribir @nombre avisa a esa persona con una notificación inmediata en vez de esperar al resumen. No crea ni una fila nueva: las menciones se leen del propio texto del mensaje al hacer el fan-out.",
  scope: "org",
  status: "beta",
  gateable: true,
  since: "2026-07",
  docsSlug: "alumno",
  requires: ["academy.classroom.chat", "academy.classroom.roster"],
  implications: {
    navHidden: [],
    flowsBlocked: ["Avisar por mención (@nombre) en el chat del aula"],
    dataPolicy: "preserved",
  },
};

/**
 * `billing.core?` es arista BLANDA a propósito: apagar la caja no debe
 * secuestrar la oferta, que degrada a «Continúa con el Nivel 2» sin precio.
 */
const PROGRESSION_UPSELL: FeatureManifest = {
  key: "academy.progression.upsell",
  name: "Oferta del siguiente nivel",
  description:
    "Al completar un nivel, ofrece el siguiente con su precio y el ahorro por adelantar. La oferta persiste hasta su caducidad o hasta que el alumno la descarte: un descuento que solo se ve una vez no convierte. Sin caja encendida, la tarjeta se muestra sin precio.",
  scope: "org",
  status: "beta",
  defaultEnabled: false,
  since: "2026-07",
  gateable: true,
  docsSlug: "caja",
  requires: ["academy.progression", "billing.core?"],
  implications: {
    navHidden: [],
    flowsBlocked: ["Descartar la oferta del siguiente nivel"],
    dataPolicy: "preserved",
  },
};

const CLASSROOM_CHAT_REACTIONS: FeatureManifest = {
  key: "academy.classroom.chat.reactions",
  name: "Reacciones del chat del aula",
  description:
    "Reaccionar con uno de seis emojis (catálogo cerrado, una reacción por persona y mensaje). Nace APAGADA a propósito: es la operación más barata de escribir y la más cara de replicar (~+20 % de entregas de fila), así que es el primer recorte ante una factura inesperada y el único que no pierde nada funcional.",
  scope: "org",
  status: "beta",
  gateable: true,
  defaultEnabled: false,
  since: "2026-07",
  docsSlug: "alumno",
  requires: ["academy.classroom.chat"],
  implications: {
    navHidden: [],
    flowsBlocked: ["Reaccionar a un mensaje del chat del aula"],
    dataPolicy: "preserved",
  },
};

/** Manifests de las verticales (fuente `academy_verticals` del catálogo). */
export function verticalManifests(): FeatureManifest[] {
  return [
    PROGRESSION,
    PROGRESSION_PATH,
    PROGRESSION_PREREQUISITES,
    PROGRESSION_PLACEMENT,
    PROGRESSION_UPSELL,
    BILLING_CORE,
    BILLING_PREPAY,
    CRM_LEADS,
    CRM_CAMPAIGNS,
    EMAIL_CAMPAIGNS,
    GAMIFICATION_CORE,
    GAMIFICATION_BADGES,
    MEDIA_RECORDING,
    FLIPPED_SESSIONS,
    AI_TUTOR,
    // B8 · un flag por capacidad de IA (el dueño quiere cortar por factura).
    AI_CHATBOT,
    AI_AUTHORING,
    AI_AUTHORING_EDIT,
    AI_AUTHORING_CONFIG,
    AI_AUTHORING_AUTOAPPLY,
    COMPLETION_RULES,
    FINAL_PROJECT,
    TUTORING,
    REST_ORG_CATALOG,
    MODALITY_IN_PERSON,
    // B2 · comunicación del aula (una entrada por etapa).
    CLASSROOM_ROSTER,
    CLASSROOM_CHAT,
    CLASSROOM_CHAT_PROMOTE,
    CLASSROOM_CHAT_MENTIONS,
    CLASSROOM_CHAT_REACTIONS,
  ];
}

/**
 * B2 · Keys de la comunicación del aula, exportadas para que nav, rutas, hooks
 * y tests las referencien **una sola vez** (nada de literales sueltos que se
 * desincronicen del manifest).
 */
export const CLASSROOM_ROSTER_KEY = CLASSROOM_ROSTER.key;
export const CLASSROOM_CHAT_KEY = CLASSROOM_CHAT.key;
export const CLASSROOM_CHAT_PROMOTE_KEY = CLASSROOM_CHAT_PROMOTE.key;
export const CLASSROOM_CHAT_MENTIONS_KEY = CLASSROOM_CHAT_MENTIONS.key;
export const CLASSROOM_CHAT_REACTIONS_KEY = CLASSROOM_CHAT_REACTIONS.key;

/**
 * §L1 · Key del gate de lo presencial. Exportada para que nav, rutas, selector
 * de modalidad y tests la referencien **una sola vez** (nada de literales
 * sueltos que se desincronicen del manifest).
 */
export const MODALITY_IN_PERSON_KEY = MODALITY_IN_PERSON.key;

/**
 * B8 · Keys de las capacidades de IA, exportadas para que la nav, las rutas, el
 * editor de cursos y los tests las referencien **una sola vez**. Un literal
 * suelto en un `<Show>` es exactamente cómo se desincroniza un gate de cliente
 * del manifest.
 */
export const AI_CHATBOT_KEY = AI_CHATBOT.key;
export const AI_AUTHORING_KEY = AI_AUTHORING.key;
export const AI_AUTHORING_EDIT_KEY = AI_AUTHORING_EDIT.key;
export const AI_AUTHORING_CONFIG_KEY = AI_AUTHORING_CONFIG.key;
export const AI_AUTHORING_AUTOAPPLY_KEY = AI_AUTHORING_AUTOAPPLY.key;

/**
 * §L2 · Modalidad de fábrica de una cohorte nueva. Online en vivo: la academia
 * es online-only, así que el camino sin decisiones lleva a lo correcto. Espejo
 * del `DEFAULT 'online_live'` de `cohort.modality` (migración 00083).
 */
export const DEFAULT_COHORT_MODALITY = "online_live" as const;

/** Modalidades que exigen presencia física (gateadas por el flag de §L1). */
export const IN_PERSON_MODALITIES: readonly string[] = ["in_person", "hybrid"];

/** ¿Esta modalidad necesita que la vertical presencial esté encendida? */
export function modalityNeedsInPerson(modality: string | null | undefined): boolean {
  return modality !== null && modality !== undefined && IN_PERSON_MODALITIES.includes(modality);
}
